import {
  useMemo,
  useRef,
  Children,
  isValidElement,
  cloneElement,
  MouseEvent,
  ReactNode,
  MouseEventHandler,
} from 'react';

import { Popover } from '@mui/material';
import clsx from 'clsx';
import { bindToggle } from 'material-ui-popup-state/core';
import { usePopupState, bindPopover } from 'material-ui-popup-state/hooks';

import { pseudoUnique } from '@cast/utils';

import { ButtonProps } from '../Button';
import {
  SplitButton,
  SplitButtonProps,
  splitButtonClasses,
} from './SplitButton';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import * as Icons from '../../icons';
import { List, ListProps } from '../../lists/List';
import { ListItem, ListItemProps } from '../../lists/ListItem';

export type ComboItemProps = ListItemProps & { main?: boolean };

const comboButtonClasses = {
  button: splitButtonClasses,
  list: { root: 'ComboButton-list', item: 'ComboButton-list-item' },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ComboItem = ({ main, className, ...props }: ComboItemProps) => {
  return (
    <ListItem
      {...props}
      className={clsx(comboButtonClasses.list.item, className)}
    />
  );
};

const sizeMapping: Record<
  Exclude<ButtonProps['size'], undefined>,
  { icon: number; iconButtonSx: SxProps }
> = {
  sm: {
    icon: 16,
    iconButtonSx: {
      pl: 6,
      pr: 8,
    },
  },
  md: {
    icon: 20,
    iconButtonSx: {
      pl: 6,
      pr: 8,
    },
  },
  lg: {
    icon: 20,
    iconButtonSx: {
      pl: 8,
      pr: 10,
    },
  },
};

export type ComboButtonProps = {
  text: string;
  size?: SplitButtonProps['size'];
  variant?: SplitButtonProps['variant'];
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  slots?: { splitButtonProps?: SplitButtonProps; listProps?: ListProps };
  children?: ReactNode;
};

export const ComboButton = ({
  text,
  size: _size,
  variant: _variant,
  onClick,
  disabled,
  children,
  slots,
}: ComboButtonProps) => {
  const splitButtonRef = useRef<HTMLDivElement>(null);

  const popupId = useMemo(pseudoUnique, []);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `${popupId}-actions`,
  });
  const Icon = popupState.isOpen ? Icons.CaretUp : Icons.CaretDown;
  const size = _size || slots?.splitButtonProps?.size || 'md';
  const variant = _variant || slots?.splitButtonProps?.variant || 'primary';

  let mainOnClick: MouseEventHandler<HTMLButtonElement> | undefined = onClick;

  const comboItems = Children.map(children, (child) => {
    if (isValidElement<ComboItemProps>(child) && child.type === ComboItem) {
      if (child.props.main && !child.props.disabled) {
        mainOnClick = child.props.onClick as
          | MouseEventHandler<HTMLButtonElement>
          | undefined;
      }
      return cloneElement(child, {
        ...child.props,
        onClick: (event: MouseEvent<HTMLLIElement>) => {
          popupState.close();
          child.props.onClick?.(event);
        },
      });
    }
    return undefined;
  });

  const sizing = sizeMapping[size];

  return (
    <>
      <SplitButton
        size={size}
        variant={variant}
        ref={splitButtonRef}
        disabled={disabled}
        {...slots?.splitButtonProps}
        slots={{
          leftButton: text,
          leftButtonProps: {
            ...slots?.splitButtonProps?.slots?.leftButtonProps,
            onClick: mainOnClick,
          },
          rightButton: <Icon size={sizing.icon} />,
          rightButtonProps: {
            ...slots?.splitButtonProps?.slots?.rightButtonProps,
            ...bindToggle(popupState),
            sx: mergeSx(
              sizing.iconButtonSx,
              slots?.splitButtonProps?.slots?.rightButtonProps?.sx
            ),
          },
        }}
      />
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -2, // Add a little gap between the button and the popover
          horizontal: 'right',
        }}
      >
        <List
          {...slots?.listProps}
          className={clsx(
            comboButtonClasses.list.root,
            slots?.listProps?.className
          )}
          sx={mergeSx(
            { minWidth: splitButtonRef.current?.offsetWidth || 'auto' },
            slots?.listProps?.sx
          )}
        >
          {comboItems}
        </List>
      </Popover>
    </>
  );
};
